import ResizableScene from "../core/ResizableScene";
import F84Game from "../Game";

export default class ScreenTransition extends ResizableScene {
	private static _instance: ScreenTransition;

	private _from: ResizableScene;
	public get from(): ResizableScene {
		return this._from;
	}
	private _to: string;
	public get to(): string {
		return this._to;
	}
	private _toData: Record<string, unknown>;
	private _onMidpoint: () => any;
	private _switched: boolean;
	private _sceneStarted: boolean;
	private _delayDisconnectPopup: boolean;

	transitionTweens: Phaser.Tweens.Tween[];

	init(data?: Record<string, unknown>): void {
		super.init(data);
		ScreenTransition._instance = this;
		this._from = <ResizableScene>data[0];
		this._to = <string>(<unknown>data[1]);
		this._toData = <Record<string, unknown>>data[2];
		this._onMidpoint = <() => any>data[3];
	}

	create(): void {
		super.create();
		this._switched = false;
		this._sceneStarted = false;
		this._delayDisconnectPopup = false;

		this.resize();

		const pieces = [];
		// yellow, blue, orange purple
		const colors = [0xffd52d, 0x76d7ff, 0xff9703, 0xb38ee8];

		const bounds = this.viewport;
		const pieceGroup = this.add.container(0, 0);

		const duration = 600;
		const delay = 100;

		for (let i = 0; i < colors.length; i++) {
			const even = i % 2 == 0;

			// sliced height and width should be the same to that it always covers the screen when the pieces rotate
			if (even) pieces.push(this.add.nineSlicedSprite(0, 0, `transitionPiece2`, 1920, 1920, 10, 1050, 768, 45));
			else pieces.push(this.add.nineSlicedSprite(0, 0, `transitionPiece1`, 1920, 1920, 750, 1075, 20, 20));

			const piece = pieces[i];
			piece.tint = colors[i];
			piece.setOrigin(1, even ? 0 : 1);

			const rotate = i > 0 && i < 3;
			if (rotate) piece.setRotation(Math.PI);

			// scale the pieces to the screen size
			const widthScale = bounds.width / piece.slicedWidth;
			const heightScale = bounds.height / piece.slicedHeight;
			const scale = Math.max(widthScale, heightScale);
			piece.setScale(scale);

			pieceGroup.add(pieces[i]);

			const xOffset = !rotate ? -1 : 1;
			const yOffset = i < 2 ? -1 : 1;

			// Set Starting position of the piece
			piece.x = pieces[i].slicedWidth * xOffset * scale;
			piece.y = pieces[i].slicedHeight * yOffset * scale;

			// Positions of pieces when their enterence is complete
			const xEnter = (pieces[i].slicedWidth / 2 - (!even ? 141 : 0)) * xOffset * scale;
			const yEnter = (pieces[i].slicedHeight / 2 - (even ? 143 : 2)) * yOffset * scale;

			if (i == 0) this.time.delayedCall(150, () => this.soundManager.play("transitionEnter"), null, this);
			this.add.tween({
				targets: pieces[i],
				x: xEnter,
				y: yEnter,
				duration: duration,
				ease: Phaser.Math.Easing.Quadratic.InOut,
				onComplete: i == 0 ? this.onTweenInComplete : null,
				onCompleteScope: i == 0 ? this : null,
			});

			this.add.tween({
				targets: pieces[i],
				y: pieces[i].y,
				x: pieces[i].x,
				duration: duration,
				delay: (duration + delay) * 2,
				ease: Phaser.Math.Easing.Quadratic.InOut,
				onComplete: this.onTransitionComplete,
				onCompleteScope: this,
			});
		}

		this.alignIn.Center(pieceGroup, this.viewport);

		// Sparkles is created here so it appears in front of the puzzle pieces
		const sparkles = this.add.lottieAnimation(0, 0, "load-sparkles", false, false);
		this.alignIn.Center(sparkles, this.viewport);

		this.time.delayedCall(duration, sparkles.play, null, sparkles);

		this.add.tween({
			targets: pieceGroup,
			rotation: Math.PI,
			duration: duration,
			delay: duration + delay,
			ease: Phaser.Math.Easing.Quadratic.InOut,
			onStart: () => this.soundManager.play("transitionExit"),
			onStartScope: this,
			// onUpdate: this.onTweenUpdate,
			// onUpdateScope: this,
		});

		this.game.input.enabled = false;
	}

	private onTweenInComplete(): void {
		// console.log(tween.progress);
		if (!this._sceneStarted) {
			this._sceneStarted = true;
		}
		if (this._switched == false) {
			this.switch();
		}
	}

	private switch(): void {
		if (this._switched) return;
		if (this._from.scene.key !== this._to) {
			this.game.scene.stop(this._from.scene.key);
			// commented-out line above relocated to here
			this.game.scene.start(this._to, this._toData);
		} else {
			this.game.scene.start(this._to, this._toData);
		}
		if (this._onMidpoint) this._onMidpoint();
		this._switched = true;
	}

	private onTransitionComplete(): void {
		this.transitionTweens = null;
		this.game.input.enabled = true;
		this.scene.stop(this.scene.key);
	}

	public static to(from: ResizableScene, to: string, data?: Record<string, unknown>, onMidpoint?: () => any): void {
		const instance = ScreenTransition._instance;
		const paused = instance && instance.time.paused;

		if (paused) {
			instance._sceneStarted = false;
			instance._switched = false;
			instance._to = to;
			instance._from = from;
			instance._toData = data;
			if (instance._onMidpoint) instance._onMidpoint();
		}

		if (instance && instance.sys.isActive) {
			instance.switch();
		}

		if (to !== from.scene.key) {
			F84Game.Instance.scene.sendToBack(to);
		}

		if (paused) {
			ScreenTransition._instance.time.paused = false;
			F84Game.Instance.scene.resume(ScreenTransition._instance);
		} else {
			F84Game.Instance.scene.bringToTop("ScreenTransition");
			F84Game.Instance.scene.start("ScreenTransition", [from, to, data, onMidpoint]);
		}
	}

	public static midpoint(from: ResizableScene, to: string, data?: Record<string, unknown>, onMidpoint?: () => any): void {
		ScreenTransition.to(from, to, data, (): void => {
			ScreenTransition._instance.time.paused = true;
			F84Game.Instance.scene.pause(ScreenTransition._instance);
			F84Game.Instance.scene.bringToTop(this._instance.to);
			if (onMidpoint) onMidpoint();
		});
	}
}
